import React, {useId, useState} from "react";
import Modal from "./Modal";
import LandingFields, {fieldsHaveErrors, cleanFieldValues} from "./LandingFields";
import {useConfirm} from "./FeedbackProvider";
import {C} from "../styles";
import BLOCK_FIELDS from "../landing/blockFields.json";
import LandingAiDraft, {isDraftable} from "./LandingAiDraft";
import LandingAiSectionDraft from "./LandingAiSectionDraft";
import {addBlock, moveBlock, removeBlock, updateBlock} from "../landing/pageModel";

const smallButton = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 13};

// Points bloquants d'un bloc (en plus des champs invalides signalés dans le formulaire) : mêmes règles que le serveur.
// Texte proposé ramené à la longueur maximale de son champ (le formulaire la fait respecter à la saisie, pas à l'ajout).
function clipToFields(fields, data) {
    const clip = (spec, value) => (typeof value === "string" && spec.max ? value.slice(0, spec.max) : value);
    return Object.fromEntries(Object.entries(data || {}).map(([key, value]) => {
        const spec = fields[key];
        if (!spec) return [key, value];
        if (Array.isArray(value) && spec.itemFields) {
            return [key, value.map(item => Object.fromEntries(Object.entries(item || {}).map(([k, v]) => [k, spec.itemFields[k] ? clip(spec.itemFields[k], v) : v])))];
        }
        return [key, clip(spec, value)];
    }));
}

function blockProblems(block) {
    const data = block.data || {};
    const problems = [];
    if (block.type === "image" && data.src && !data.alt && data.decorative !== "oui") {
        problems.push("Texte alternatif requis (ou marquez l'image comme décorative)");
    }
    if (block.type === "gallery" && Array.isArray(data.items) && data.items.some(item => item.src && !item.alt)) {
        problems.push("Texte alternatif requis pour chaque image de la galerie");
    }
    if (block.type === "button" && data.target === "link" && !data.url) {
        problems.push("Lien requis pour un bouton « lien »");
    }
    if (fieldsHaveErrors(BLOCK_FIELDS.blocks[block.type].fields, data)) problems.push("Un champ de ce bloc est invalide");
    return problems;
}

// Éditeur d'une rubrique libre : réglages (nom, fond, colonnes, alignement, animation) puis liste de blocs (ajout, ordre,
// suppression, formulaire généré depuis le registre de chaque type). Travaille sur une copie ; rien n'est enregistré avant
// « Enregistrer » (Échap / Annuler ferment sans rien changer).
export default function LandingCustomEditor({section, onSave, onClose, aiAvailable = false}) {
    const idBase = useId();
    const confirm = useConfirm();
    const [page, setPage] = useState({schema: 1, sections: [section]});
    const [openId, setOpenId] = useState(null);
    const [newType, setNewType] = useState("");

    const current = page.sections[0];
    const blocks = (current.blocks || []).filter(b => BLOCK_FIELDS.blocks[b.type]);
    const problemsById = Object.fromEntries(blocks.map(b => [b.id, blockProblems(b)]));
    const badBlocks = blocks.filter(b => problemsById[b.id].length > 0).length;
    const full = blocks.length >= BLOCK_FIELDS.maxBlocksPerSection;

    function add() {
        if (!newType) return;
        const {page: next, blockId} = addBlock(page, current.id, newType);
        if (!blockId) return;
        setPage(next);
        setOpenId(blockId);
        setNewType("");
    }

    // Ajoute à la fin les blocs proposés par l'IA (identifiants uniques et données remplis par l'éditeur) ; rien n'est enregistré.
    function addProposed({name, blocks: proposed}) {
        setPage(p => {
            let next = p;
            for (const {type, data} of proposed) {
                const {page: withBlock, blockId} = addBlock(next, current.id, type);
                if (!blockId) break;
                next = updateBlock(withBlock, current.id, blockId, clipToFields(BLOCK_FIELDS.blocks[type].fields, data));
            }
            if (name) {
                const section = next.sections[0];
                next = {...next, sections: [{...section, settings: {...(section.settings || {}), name}}]};
            }
            return next;
        });
    }

    async function remove(block, index) {
        if (!(await confirm(`Supprimer le bloc ${index + 1} (${BLOCK_FIELDS.blocks[block.type].label}) ?`, {danger: true}))) return;
        setPage(p => removeBlock(p, current.id, block.id));
    }

    function handleSave() {
        onSave({
            ...current,
            settings: cleanFieldValues(current.settings || {}),
            blocks: blocks.map(b => ({...b, data: cleanFieldValues(b.data || {})})),
        });
    }

    return (
        <Modal title="Modifier la rubrique libre" onClose={onClose} floating>
            <h3 style={{margin: "0 0 8px"}}>Réglages</h3>
            <LandingFields
                fields={BLOCK_FIELDS.settings}
                values={current.settings || {}}
                defaults={{name: "Nouvelle rubrique"}}
                idBase={`${idBase}-settings`}
                onChange={(settings) => setPage({...page, sections: [{...current, settings}]})}
            />

            <h3 style={{margin: "16px 0 8px"}}>Blocs ({blocks.length}/{BLOCK_FIELDS.maxBlocksPerSection})</h3>
            {blocks.map((block, i) => {
                const spec = BLOCK_FIELDS.blocks[block.type];
                const problems = problemsById[block.id];
                const open = openId === block.id;
                return (
                    <div key={block.id} data-testid="custom-block" style={{border: `1px solid ${problems.length ? C.danger : C.border}`, borderRadius: 8, padding: 10, marginBottom: 8}}>
                        <div style={{display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap"}}>
                            <strong style={{flex: 1}}>{spec.label}</strong>
                            <button type="button" style={smallButton} aria-label={`Modifier le bloc ${i + 1}`} aria-expanded={open}
                                    onClick={() => setOpenId(open ? null : block.id)}>{open ? "Replier" : "Modifier"}</button>
                            <button type="button" style={smallButton} aria-label={`Monter le bloc ${i + 1}`} disabled={i === 0}
                                    onClick={() => setPage(p => moveBlock(p, current.id, block.id, -1))}>↑</button>
                            <button type="button" style={smallButton} aria-label={`Descendre le bloc ${i + 1}`} disabled={i === blocks.length - 1}
                                    onClick={() => setPage(p => moveBlock(p, current.id, block.id, 1))}>↓</button>
                            <button type="button" style={smallButton} aria-label={`Supprimer le bloc ${i + 1} (${spec.label})`}
                                    onClick={() => remove(block, i)}>✕</button>
                        </div>
                        {problems.map(p => <p key={p} role="alert" style={{color: C.danger, margin: "6px 0 0", fontSize: 13}}>{p}</p>)}
                        {open && (
                            <div style={{marginTop: 10}}>
                                {isDraftable("block", block.type) && (
                                    <LandingAiDraft
                                        scope="block"
                                        type={block.type}
                                        current={block.data || {}}
                                        disabled={!aiAvailable}
                                        onApply={(proposed) => setPage(p => updateBlock(p, current.id, block.id, {...(block.data || {}), ...proposed}))}
                                    />
                                )}
                                {Object.keys(spec.fields).length === 0
                                    ? <p style={{margin: 0, fontSize: 13}}>Ce bloc n'a aucun réglage.</p>
                                    : <LandingFields
                                        fields={spec.fields}
                                        values={block.data || {}}
                                        defaults={{}}
                                        idBase={`${idBase}-${block.id}`}
                                        onChange={(data) => setPage(p => updateBlock(p, current.id, block.id, data))}
                                    />}
                            </div>
                        )}
                    </div>
                );
            })}

            <div style={{display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8}}>
                <select aria-label="Type de bloc à ajouter" value={newType} onChange={e => setNewType(e.target.value)}
                        style={{padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 14}}>
                    <option value="">Choisir un type de bloc…</option>
                    {BLOCK_FIELDS.groups.map(group => (
                        <optgroup key={group.id} label={group.label}>
                            {group.types.map(type => <option key={type} value={type}>{BLOCK_FIELDS.blocks[type].label}</option>)}
                        </optgroup>
                    ))}
                </select>
                <button type="button" style={smallButton} disabled={!newType || full} onClick={add}>+ Ajouter un bloc</button>
                {full && <span style={{fontSize: 12}}>30 blocs maximum</span>}
                <LandingAiSectionDraft
                    disabled={!aiAvailable}
                    remainingBlocks={BLOCK_FIELDS.maxBlocksPerSection - blocks.length}
                    onAdd={addProposed}
                />
            </div>

            {badBlocks > 0 && (
                <p role="status" style={{color: C.danger, margin: "12px 0 0", fontSize: 13}}>
                    {badBlocks} bloc{badBlocks > 1 ? "s" : ""} à corriger avant d'enregistrer
                </p>
            )}

            <div style={{display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16}}>
                <button type="button" onClick={onClose} style={smallButton}>Annuler</button>
                <button type="button" onClick={handleSave} disabled={badBlocks > 0}
                        style={{...smallButton, background: badBlocks > 0 ? "#ccc" : C.primary, color: "#fff", border: "none", cursor: badBlocks > 0 ? "not-allowed" : "pointer"}}>
                    Enregistrer
                </button>
            </div>
        </Modal>
    );
}
